/**
 * Map Runtime Health components to Action Framework remediations.
 * Canonical action IDs match control_plane.services.actions.registry
 * (aliases like run-backup still accepted by the API).
 */

import type { RuntimeHealthComponent } from "./runtimeDomains";
import type { HealthLabel, NotMonitoredLabel } from "./types";

export type AutomationLevel = "safe" | "confirm" | "manual";
export type ActionRisk = "safe" | "low" | "medium" | "high";

export type RemediationAction = {
  id: string;
  label: string;
  primary?: boolean;
  destructive?: boolean;
  automationLevel?: AutomationLevel;
  risk?: ActionRisk;
  estimatedDurationSeconds?: number | null;
  requiresConfirmation?: boolean;
  /** Optional params for Action Framework execute (e.g. integration_id). */
  params?: Record<string, string>;
};

/** Static mirror of backend registry metadata for offline RH render. */
export const ACTION_CATALOG: Record<
  string,
  Omit<RemediationAction, "id" | "primary" | "destructive" | "params">
> = {
  "backup.run": {
    label: "Run Backup",
    automationLevel: "safe",
    risk: "safe",
    estimatedDurationSeconds: 120,
    requiresConfirmation: false,
  },
  "tenant.restart": {
    label: "Restart API",
    automationLevel: "confirm",
    risk: "low",
    estimatedDurationSeconds: 60,
    requiresConfirmation: true,
  },
  "tenant.redeploy": {
    label: "Redeploy Tenant",
    automationLevel: "confirm",
    risk: "medium",
    estimatedDurationSeconds: 300,
    requiresConfirmation: true,
  },
  "email.reconnect": {
    label: "Retry Email",
    automationLevel: "safe",
    risk: "safe",
    estimatedDurationSeconds: 60,
    requiresConfirmation: false,
  },
  "verification.rerun": {
    label: "Run Doctor",
    automationLevel: "safe",
    risk: "safe",
    estimatedDurationSeconds: 90,
    requiresConfirmation: false,
  },
  "runtime.refresh": {
    label: "Re-verify",
    automationLevel: "safe",
    risk: "safe",
    estimatedDurationSeconds: 60,
    requiresConfirmation: false,
  },
  "knowledgebase.reindex": {
    label: "Reindex",
    automationLevel: "safe",
    risk: "safe",
    estimatedDurationSeconds: 180,
    requiresConfirmation: false,
  },
  "dns.reverify": {
    label: "Re-run Verification",
    automationLevel: "safe",
    risk: "safe",
    estimatedDurationSeconds: 30,
    requiresConfirmation: false,
  },
  "tls.reverify": {
    label: "Re-run Verification",
    automationLevel: "safe",
    risk: "safe",
    estimatedDurationSeconds: 30,
    requiresConfirmation: false,
  },
  "integration.test": {
    label: "Test Connection",
    automationLevel: "safe",
    risk: "safe",
    estimatedDurationSeconds: 45,
    requiresConfirmation: false,
  },
  "shopify.sync": {
    label: "Sync Shopify",
    automationLevel: "safe",
    risk: "safe",
    estimatedDurationSeconds: 45,
    requiresConfirmation: false,
  },
};

/** Legacy kebab → canonical. */
export const ACTION_ID_ALIASES: Record<string, string> = {
  "run-backup": "backup.run",
  "restart-tenant-api": "tenant.restart",
  "redeploy-tenant": "tenant.redeploy",
  "retry-email": "email.reconnect",
  "run-doctor": "verification.rerun",
  "run-tenant-doctor": "runtime.refresh",
};

export function canonicalizeActionId(actionId: string): string {
  const key = actionId.trim().toLowerCase();
  return ACTION_ID_ALIASES[key] ?? key;
}

export function withCatalogMeta(action: RemediationAction): RemediationAction {
  const id = canonicalizeActionId(action.id);
  const meta = ACTION_CATALOG[id];
  if (!meta) return { ...action, id };
  return {
    ...action,
    id,
    label: action.label || meta.label,
    automationLevel: action.automationLevel ?? meta.automationLevel,
    risk: action.risk ?? meta.risk,
    estimatedDurationSeconds:
      action.estimatedDurationSeconds ?? meta.estimatedDurationSeconds,
    requiresConfirmation: action.requiresConfirmation ?? meta.requiresConfirmation,
  };
}

/** RH component id → Doctor / attention check_id used in incident ids `{slug}:{check}`. */
export const COMPONENT_ATTENTION_CHECK: Record<string, string> = {
  concierge_runtime: "tenant.health",
  postgresql: "database.connect",
  disk: "host.disk_space",
  storage: "host.docker_storage",
  memory: "host.memory",
  docker: "host.docker",
  cpu: "host.cpu",
  worker: "tenant.worker",
  email_processing: "tenant.email_processing",
  control_plane_backend: "platform.control_plane_backend",
  operations_api: "platform.operations_api",
  authentication: "platform.authentication",
  internal_platform_services: "platform.internal_services",
  knowledge_base: "knowledge",
  dns_connectivity: "dns",
  tls_certificate: "tls",
};

const UNHEALTHY: ReadonlySet<HealthLabel | NotMonitoredLabel> = new Set([
  "Critical",
  "Degraded",
  "Unknown",
]);

function needsRemediation(label: HealthLabel | NotMonitoredLabel): boolean {
  return UNHEALTHY.has(label);
}

function catalogAction(
  id: string,
  opts?: Partial<RemediationAction>,
): RemediationAction {
  return withCatalogMeta({ id, label: ACTION_CATALOG[id]?.label ?? id, ...opts });
}

/**
 * Executable actions from the Action Framework registry.
 * Returns [] when the platform has no automated remediation for this component.
 */
export function remediationActionsForComponent(
  component: RuntimeHealthComponent,
): RemediationAction[] {
  if (!needsRemediation(component.label) && component.presence !== "unavailable") {
    return [];
  }

  const id = component.id;
  const actions: RemediationAction[] = [];

  const add = (action: RemediationAction) => {
    const next = withCatalogMeta(action);
    if (!actions.some((a) => a.id === next.id)) actions.push(next);
  };

  if (id === "concierge_runtime" || id === "postgresql" || id === "worker") {
    add(catalogAction("tenant.restart", { primary: true }));
    if (id === "concierge_runtime" || id === "postgresql") {
      add(catalogAction("tenant.redeploy", { destructive: true }));
    }
  } else if (id === "email_processing" || id === "smtp") {
    add(catalogAction("email.reconnect", { primary: true }));
    add(catalogAction("tenant.restart"));
  } else if (id === "knowledge_base") {
    add(catalogAction("knowledgebase.reindex", { primary: true }));
  } else if (id === "dns_connectivity") {
    add(catalogAction("dns.reverify", { primary: true }));
  } else if (id === "tls_certificate") {
    add(catalogAction("tls.reverify", { primary: true }));
  } else if (
    id === "control_plane_backend" ||
    id === "operations_api" ||
    id === "authentication" ||
    id === "internal_platform_services" ||
    id === "disk" ||
    id === "memory" ||
    id === "docker" ||
    id === "cpu"
  ) {
    add(catalogAction("verification.rerun", { primary: true }));
  } else if (id.startsWith("integration_") || id === "openai" || id === "shopify") {
    if (id === "shopify" || id.endsWith("shopify")) {
      add(catalogAction("shopify.sync", { primary: true }));
    } else {
      const integrationId = id.replace(/^integration_/, "") || id;
      add(
        catalogAction("integration.test", {
          primary: true,
          label: "Test API Key",
          params: { integration_id: integrationId === "openai" ? "openai" : integrationId },
        }),
      );
    }
  }

  const rec = (component.recommendation ?? "").toLowerCase();
  if (rec.includes("backup") || rec.includes("run backup")) {
    add(catalogAction("backup.run", { primary: true }));
  }
  if (rec.includes("reindex")) {
    add(catalogAction("knowledgebase.reindex", { primary: true }));
  }

  return actions;
}

/**
 * Resolve the existing incident id for an action against a tenant.
 * Prefers live attention ids; falls back to canonical ids the backend accepts.
 */
export function incidentIdForRemediation(input: {
  actionId: string;
  tenantSlug: string;
  attentionIds?: string[];
  componentId?: string | null;
}): string {
  const actionId = canonicalizeActionId(input.actionId);
  const { tenantSlug, attentionIds = [], componentId } = input;
  const slug = tenantSlug.trim();

  if (actionId === "verification.rerun") {
    return (
      attentionIds.find((id) => id.startsWith("task:") && id.includes("doctor")) ??
      attentionIds.find((id) => id.startsWith("host:")) ??
      "task:run-doctor"
    );
  }

  if (actionId === "backup.run") {
    return (
      attentionIds.find((id) => id === `${slug}:policy.backup`) ??
      attentionIds.find((id) => id.endsWith(":policy.backup")) ??
      attentionIds.find((id) => id.endsWith(":backup-overdue")) ??
      attentionIds.find((id) => id === "task:backup-overdue") ??
      `${slug}:policy.backup`
    );
  }

  if (componentId) {
    const check = COMPONENT_ATTENTION_CHECK[componentId];
    if (check) {
      const preferred = `${slug}:${check}`;
      if (attentionIds.includes(preferred)) return preferred;
      const hostPreferred = `host:${check}`;
      if (attentionIds.includes(hostPreferred)) return hostPreferred;
      if (check.startsWith("host.") || check.startsWith("platform.")) {
        return attentionIds.find((id) => id.endsWith(`:${check}`)) ?? `host:${check}`;
      }
      return preferred;
    }
  }

  const tenantAttention = attentionIds.find((id) => id.startsWith(`${slug}:`));
  if (tenantAttention) return tenantAttention;

  if (actionId === "email.reconnect") return `${slug}:tenant.health`;
  if (actionId === "tenant.redeploy") return `${slug}:tenant.manifest_runtime`;
  return `${slug}:tenant.health`;
}
